import { Injectable, signal } from '@angular/core';
import { WriteMethod, WriteQueueEntry, openOfflineDb } from './db';
import { SERVER_COMPUTED_FIELDS, getResponseShape, resolveEntityType } from './response-shapes';
import { SELECTORS_PREFETCH_KEY } from './offline-cache.service';
import { Urls } from '@/apiConnection/urls';

@Injectable({
    providedIn: 'root'
})
export class OfflineQueueService {

    /** Live count of queue entries needing attention (pending/syncing/failed), for the UI badge. */
    pendingCount = signal(0);

    private lastTempId = 0;

    constructor() {
        this.refreshPendingCount();
    }

    async refreshPendingCount(): Promise<void> {
        const db = await openOfflineDb();
        const all = await db.getAll('write_queue');
        this.pendingCount.set(all.filter(e => e.status !== 'synced').length);
    }

    /**
     * Queues a save (create/update) made while offline. Returns a response synthesized to
     * look like the real backend's, so the calling form's `next()` fires as if the write
     * succeeded (with a temp negative id standing in for a create).
     */
    async enqueueSave(url: string, body: any, id: number | string): Promise<any> {
        const entityType = resolveEntityType(url) ?? url;
        const isCreate = !id || id === 0;
        const tempId = isCreate ? this.nextTempId() : null;
        const method: WriteMethod = isCreate ? 'POST' : 'PUT';
        const targetUrl = isCreate ? url : `${url}/${id}`;

        await this.add({
            method,
            url: targetUrl,
            body,
            entityType,
            tempId,
            status: 'pending',
            attempts: 0,
            lastError: null,
            createdAt: Date.now(),
            updatedAt: Date.now(),
        });

        const effectiveId = isCreate ? tempId! : Number(id);
        return this.synthesizeResponse(entityType, effectiveId, body);
    }

    async enqueueDelete(url: string, id: number): Promise<any> {
        const entityType = resolveEntityType(url) ?? url;
        await this.add({
            method: 'DELETE',
            url: `${url}/${id}`,
            body: null,
            entityType,
            tempId: null,
            status: 'pending',
            attempts: 0,
            lastError: null,
            createdAt: Date.now(),
            updatedAt: Date.now(),
        });
        return { status: 'success', message: 'Pendiente de sincronización' };
    }

    /**
     * Temp ids key id_map, so two creates must never share one. A bare `-Date.now()` collides for
     * two creates in the same millisecond; instead use microsecond-scale ms*1000 plus a random
     * sub-ms part (still well inside Number.MAX_SAFE_INTEGER), and force it strictly below the
     * last one issued here so it's also monotonic within this tab.
     */
    private nextTempId(): number {
        const candidate = -(Date.now() * 1000 + Math.floor(Math.random() * 1000));
        this.lastTempId = Math.min(candidate, this.lastTempId - 1);
        return this.lastTempId;
    }

    private async add(entry: WriteQueueEntry): Promise<void> {
        const db = await openOfflineDb();
        await db.add('write_queue', entry);
        await this.refreshPendingCount();
    }

    private synthesizeResponse(entityType: string, id: number, body: any): any {
        const shape = getResponseShape(entityType);
        const cleanBody = { ...body };
        for (const field of SERVER_COMPUTED_FIELDS) {
            delete cleanBody[field];
        }
        const entity = { id, ...cleanBody };

        if (shape.family === 'wrapped') {
            return { status: 'success', message: 'Pendiente de sincronización', [shape.key!]: entity };
        }
        if (shape.family === 'message-only') {
            return { message: 'Pendiente de sincronización' };
        }
        return entity;
    }

    // --- getById resolution for a temp (negative) id, and open-form reconciliation ---

    /** Real id a temp id has already been resolved to, if the queue entry already synced. */
    async getRealIdForTemp(tempId: number): Promise<number | null> {
        const db = await openOfflineDb();
        const mapped = await db.get('id_map', tempId);
        return mapped?.realId ?? null;
    }

    /** Synthesized entity for a still-pending temp id, read straight from its queue entry, shaped like the real getById endpoint (not the save endpoint - they differ, see below). */
    async getQueuedEntity(entityType: string, tempId: number): Promise<any | null> {
        const db = await openOfflineDb();
        const entries = await db.getAllFromIndex('write_queue', 'by-entity-temp', [entityType, tempId]);
        const entry = entries[0];
        if (!entry) return null;
        const response = this.synthesizeGetByIdResponse(entityType, tempId, entry.body);
        await this.attachRelations(entityType, entry.body, response);
        return response;
    }

    /**
     * The real documents/purchases getById also returns the related client/provider and
     * pay_method as top-level siblings, and the print templates dereference them unguarded
     * (`{{ client.name }}`) - so a queued one must carry them too or printing it offline
     * renders blank. Best-effort from the read cache: the full record if its own getById was
     * cached, else the prefetched selectors catalog row, else an empty object.
     */
    private async attachRelations(entityType: string, body: any, response: any): Promise<void> {
        if (!['documents', 'purchases', 'tickets'].includes(entityType)) return;
        const db = await openOfflineDb();
        const cached = async (url: string) => (await db.get('read_cache', `GET:${url}`))?.response;
        const prefetch = (await db.get('read_cache', SELECTORS_PREFETCH_KEY))?.response ?? {};
        const findIn = (list: any[] | undefined, id: any) => id == null ? null : (list ?? []).find(row => row.id == id) ?? null;

        // Each real line is the full product row; the templates branch on `line.code` (a product
        // line vs. an albarán group) and read `line.measurement.short_name` unguarded.
        const details: any[] = response.document_details ?? response.purchase_details ?? response.ticket_details ?? [];
        for (const line of details) {
            const product = findIn(prefetch.products, line.id);
            Object.assign(line, {
                ...product,
                id: line.id,
                name: line.name ?? product?.name,
                code: product?.code ?? line.code ?? String(line.id),
                measurement: findIn(prefetch.measurements, product?.measurement_id) ?? {},
                pivot: line.pivot,
            });
        }
        if (entityType === 'tickets') return;

        response.pay_method = findIn(prefetch.pay_methods, body?.pay_id);

        if (entityType === 'documents') {
            const full = body?.client_id != null ? (await cached(`${Urls.urls.clients}/${body.client_id}`))?.client : null;
            const row = findIn(prefetch.clients, body?.client_id);
            // The selectors catalog aliases phone_1 as `phone`; the template reads `email`.
            response.client = full ?? (row ? { ...row, email: row.email_1 } : {});
            response.tax = null;
        } else {
            const full = body?.provider_id != null ? (await cached(`${Urls.urls.providers}/${body.provider_id}`))?.provider : null;
            response.provider = full ?? findIn(prefetch.providers, body?.provider_id) ?? {};
        }
    }

    /**
     * documents/tickets/purchases forms save a flat `items[]` array, but their real getById
     * endpoint returns each line as the full product entity plus a `.pivot` (an Eloquent
     * belongsToMany-through-pivot shape) - and the details array itself sits in a different
     * place per entity (nested under `document`, but a top-level sibling for ticket/purchase).
     * Reconstructing that shape here is what lets a document/ticket/purchase created offline
     * be reopened for editing (via `refresh()`'s reload) before it has synced.
     */
    private synthesizeGetByIdResponse(entityType: string, id: number, body: any): any {
        if (!['documents', 'tickets', 'purchases'].includes(entityType)) {
            // Unlike the save-response synthesis, this always echoes the saved fields back -
            // even for the 'message-only' family (providers/pay_methods). That family's real
            // backend gap is about the *save* response; there's nothing stopping us from
            // showing the user their own not-yet-synced data locally.
            const shape = getResponseShape(entityType);
            const cleanBody = { ...body };
            for (const field of SERVER_COMPUTED_FIELDS) delete cleanBody[field];
            const entity = { id, ...cleanBody };
            return shape.family === 'wrapped' && shape.key
                ? { status: 'success', [shape.key]: entity }
                : entity;
        }

        const cleanBody = { ...body };
        for (const field of SERVER_COMPUTED_FIELDS) delete cleanBody[field];
        const items = cleanBody.items ?? [];
        delete cleanBody.items;
        const entity = { id, ...cleanBody };
        const details = (items as any[]).map(item => ({
            id: item.product_id,
            name: item.name,
            pivot: {
                id: item.id ?? null,
                document_id: item.id ?? null,
                product_id: item.product_id,
                quantity: item.quantity,
                discount: item.discount,
                price: item.price,
                special_measures: item.special_measures ?? null,
                tax: item.tax,
                apply_tax: item.apply_tax ?? null,
                personalized_desc: item.personalized_desc ?? null,
            },
        }));

        if (entityType === 'documents') {
            // documents-form reads `document.document_details`; the print templates read the
            // top-level `document_details` the real endpoint returns - provide both.
            return { status: 'success', document: { ...entity, document_details: details }, document_details: details };
        }
        if (entityType === 'tickets') {
            return { status: 'success', ticket: entity, ticket_details: details };
        }
        return { status: 'success', purchase: entity, purchase_details: details };
    }

    async mapId(tempId: number, realId: number, entityType: string): Promise<void> {
        const db = await openOfflineDb();
        await db.put('id_map', { tempId, realId, entityType, resolvedAt: Date.now() });
    }

    async getAllIdMappings(): Promise<Map<number, number>> {
        const db = await openOfflineDb();
        const all = await db.getAll('id_map');
        return new Map(all.map(e => [e.tempId, e.realId]));
    }

    /**
     * Atomically claims the oldest 'pending' entry (FIFO by id) by flipping it to 'syncing'
     * inside a single IndexedDB transaction - the browser serializes readwrite transactions
     * on the same store even across tabs, so two tabs racing to replay can't both claim the
     * same entry. Soft lock only: a tab that crashes mid-sync leaves the entry stuck at
     * 'syncing' rather than releasing it (named, accepted limitation).
     */
    async claimNextPending(): Promise<WriteQueueEntry | null> {
        const db = await openOfflineDb();
        const tx = db.transaction('write_queue', 'readwrite');
        const cursor = await tx.store.index('by-status').openCursor(IDBKeyRange.only('pending'));
        if (!cursor) {
            await tx.done;
            return null;
        }
        const entry = cursor.value;
        entry.status = 'syncing';
        entry.attempts += 1;
        entry.updatedAt = Date.now();
        await cursor.update(entry);
        await tx.done;
        await this.refreshPendingCount();
        return entry;
    }

    // --- queue inspection, used by the sync-status page, the sync engine and the badge ---

    async list(): Promise<WriteQueueEntry[]> {
        const db = await openOfflineDb();
        const all = await db.getAll('write_queue');
        return all.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    }

    async listPending(): Promise<WriteQueueEntry[]> {
        const all = await this.list();
        return all.filter(e => e.status === 'pending');
    }

    async get(id: number): Promise<WriteQueueEntry | undefined> {
        const db = await openOfflineDb();
        return db.get('write_queue', id);
    }

    /** Finds a not-yet-synced queue entry for a given entity - either a create (matched by temp id) or an update/delete queued against an already-real id, for the inline "pendiente de sincronizar" banner on a form. */
    async findPendingForEntity(entityType: string, id: number): Promise<WriteQueueEntry | undefined> {
        const all = await this.list();
        return all.find(e => e.status !== 'synced' && e.entityType === entityType &&
            (e.tempId === id || e.url.endsWith('/' + id)));
    }

    async remove(id: number): Promise<void> {
        const db = await openOfflineDb();
        await db.delete('write_queue', id);
        await this.refreshPendingCount();
    }

    async update(entry: WriteQueueEntry): Promise<void> {
        const db = await openOfflineDb();
        entry.updatedAt = Date.now();
        await db.put('write_queue', entry);
        await this.refreshPendingCount();
    }
}
